import { useEffect, useState, type JSX } from "react";
import { LOCAL, MOCK, REFRESH_EVENT, useDashboard, type ConsoleUser, type Provider } from "./data.js";
import { Icon, type IconName } from "./ui.js";
import { Home } from "./pages/Home.js";
import { Usage } from "./pages/Usage.js";
import { Models } from "./pages/Models.js";
import { Detail } from "./pages/Detail.js";
import { Stats } from "./pages/Stats.js";
import { Settings } from "./pages/Settings.js";
import "./console.css";

export type { ConsoleUser, UsageWindow } from "./data.js";

type Tab = "home" | "usage" | "models" | "stats" | "settings";

const TABS: Array<{ id: Tab; label: string; icon: IconName }> = [
  { id: "home", label: "首页", icon: "home" },
  { id: "usage", label: "用量", icon: "usage" },
  { id: "models", label: "模型", icon: "models" },
  { id: "stats", label: "统计", icon: "stats" },
  { id: "settings", label: "设置", icon: "settings" },
];

function Avatar({ user }: { user: ConsoleUser }): JSX.Element {
  const src = user.avatarSrc ?? (user.avatarVersion ? `/v1/profile/avatar?v=${encodeURIComponent(user.avatarVersion)}` : null);
  return (
    <div className="cx-me">
      <div className="cx-avatar">{src ? <img src={src} alt="" /> : <span>{user.displayName.slice(0, 1)}</span>}</div>
      <b title={user.displayName}>{user.displayName}</b>
    </div>
  );
}

export function Console({ user, mode = "web" }: { user: ConsoleUser; mode?: "web" | "display" }): JSX.Element {
  const [tab, setTab] = useState<Tab>(() => {
    if (!LOCAL) return "home";
    const value = new URLSearchParams(window.location.search).get("tab");
    return value === "usage" || value === "models" || value === "stats" || value === "settings" ? value : "home";
  });
  const [usageDays, setUsageDays] = useState(7);
  const [statsDays, setStatsDays] = useState(7);
  const [detail, setDetail] = useState<Provider | null>(null);
  const { data, error, reload } = useDashboard();
  const refresh = (): void => { reload(); window.dispatchEvent(new Event(REFRESH_EVENT)); };
  const [clock, setClock] = useState(Date.now());
  useEffect(() => { const timer = window.setInterval(() => setClock(Date.now()), 30_000); return () => window.clearInterval(timer); }, []);
  // Display tokens are read-only, so a paired device hides 设置 and every management entry. The localhost mock
  // preview (which main.tsx renders in display mode) shows the full web navigation so it can be compared with the comps.
  const web = mode === "web" || MOCK;
  const tabs = web ? TABS : TABS.filter((item) => item.id !== "settings");
  const page = detail ? "detail" : tab;

  return (
    <div className="cx-root">
      <div className="cx-shell">
        <nav className="cx-side">
          <Avatar user={user} />
          <div className="cx-nav">
            {tabs.map((item) => (
              <button key={item.id} className={tab === item.id && !detail ? "on" : ""} onClick={() => { setDetail(null); setTab(item.id); }}>
                <Icon name={item.icon} /><span>{item.label}</span>
              </button>
            ))}
          </div>
        </nav>
        <main className={`cx-main page-${page}`}>
          <div className="cx-hero" aria-hidden="true" />
          <div className="cx-page">
            {detail ? <Detail provider={detail} onBack={() => setDetail(null)} dashboard={data} />
              : tab === "home" ? <Home dashboard={data} error={error} clock={clock} onRefresh={refresh} onDetail={setDetail} />
              : tab === "usage" ? <Usage days={usageDays} onDays={setUsageDays} dashboard={data} />
              : tab === "models" ? <Models canAdd={web} onConfigure={() => setTab("settings")} onDetail={setDetail} />
              : tab === "stats" ? <Stats days={statsDays} onDays={setStatsDays} />
              : <Settings user={user} manage={web} onSync={refresh} />}
          </div>
        </main>
      </div>
    </div>
  );
}
